import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    CardMedia,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { CloudUpload, Delete, Visibility } from "@mui/icons-material";
import dayjs from "dayjs";
import { bulkUploadAds } from "../../../api/api";

const BulkUpload = ({ onClose, onSuccess }) => {
    const [imageType, setImageType] = useState("landscape");
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploadProgress, setUploadProgress] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const handleImageTypeChange = (event) => {
        setImageType(event.target.value);
        // Clear files when changing type
        setFiles([]);
        setPreviews([]);
        setError(null);
        setSuccess(null);
    };

    const handleFileSelect = (selectedFiles) => {
        const imageFiles = Array.from(selectedFiles).filter((file) =>
            file.type.startsWith("image/")
        );

        if (imageFiles.length === 0) {
            setError("Please select valid image files");
            return;
        }

        // Sort files by name to maintain order
        imageFiles.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true })
        );

        setFiles(imageFiles);

        // Create previews
        const newPreviews = imageFiles.map((file, index) => ({
            id: index,
            file,
            preview: URL.createObjectURL(file),
            title: `Ad ${index + 1}`,
            description: "",
            target_url: "",
        }));

        setPreviews(newPreviews);
        setError(null);
        setSuccess(null);
    };

    const handleFileInputChange = (event) => {
        handleFileSelect(event.target.files);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragOver(false);
        handleFileSelect(event.dataTransfer.files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setDragOver(false);
    };

    const updatePreview = (index, field, value) => {
        setPreviews((prev) =>
            prev.map((preview, i) =>
                i === index ? { ...preview, [field]: value } : preview
            )
        );
    };

    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        const newPreviews = previews.filter((_, i) => i !== index);

        // Reorder remaining files
        const reorderedPreviews = newPreviews.map((preview, i) => ({
            ...preview,
            id: i,
            title: `Ad ${i + 1}`,
        }));

        setFiles(newFiles);
        setPreviews(reorderedPreviews);
    };

    const handleSubmit = async () => {
        if (previews.length === 0) {
            setError("Please select at least one image");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setUploadProgress("Preparing upload...");

        try {
            const adsData = previews.map((preview, index) => ({
                title: preview.title,
                description: preview.description,
                image_type: imageType,
                start_date: dayjs().format("YYYY-MM-DD"),
                end_date: dayjs().add(30, "day").format("YYYY-MM-DD"),
                sort_order: index,
                is_active: true,
                target_url: preview.target_url,
                image: preview.file,
            }));

            setUploadProgress(`Uploading ${previews.length} ads...`);
            const response = await bulkUploadAds(adsData);

            if (response.success) {
                setSuccess(
                    response.message ||
                        `Successfully uploaded ${previews.length} ${imageType} ads`
                );
                setUploadProgress("");
                setTimeout(() => {
                    onSuccess(response.data);
                    onClose();
                }, 2000);
            } else {
                setError(response.message || "Failed to upload ads");
                setUploadProgress("");
            }
        } catch (err) {
            setError("Error uploading ads: " + err.message);
            setUploadProgress("");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Clean up object URLs
        previews.forEach((preview) => {
            URL.revokeObjectURL(preview.preview);
        });
        onClose();
    };

    return (
        <Dialog open={true} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h5" component="div">
                    Bulk Upload Ads
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    Upload multiple images at once. Images will be automatically
                    numbered for sort order.
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Image Type</InputLabel>
                        <Select
                            value={imageType}
                            onChange={handleImageTypeChange}
                            label="Image Type"
                        >
                            <MenuItem value="landscape">Landscape</MenuItem>
                            <MenuItem value="portrait">Portrait</MenuItem>
                        </Select>
                    </FormControl>

                    {/* File Upload Area */}
                    <Box
                        sx={{
                            border: `2px dashed ${
                                dragOver ? "primary.main" : "grey.300"
                            }`,
                            borderRadius: 2,
                            p: 4,
                            textAlign: "center",
                            backgroundColor: dragOver
                                ? "action.hover"
                                : "background.paper",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                        }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() =>
                            document.getElementById("file-input").click()
                        }
                    >
                        <CloudUpload
                            sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            Drop images here or click to select
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Select multiple {imageType} images. They will be
                            automatically numbered.
                        </Typography>
                        <input
                            id="file-input"
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileInputChange}
                            style={{ display: "none" }}
                        />
                    </Box>
                </Box>

                {/* Error/Success Messages */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {uploadProgress && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {uploadProgress}
                    </Alert>
                )}

                {/* Image Previews */}
                {previews.length > 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Preview ({previews.length} images)
                        </Typography>
                        <Grid container spacing={2}>
                            {previews.map((preview, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={preview.id}
                                >
                                    <Card>
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={preview.preview}
                                            alt={preview.title}
                                            sx={{ objectFit: "cover" }}
                                        />
                                        <CardContent>
                                            <Box sx={{ mb: 2 }}>
                                                <Chip
                                                    label={`Sort Order: ${
                                                        index + 1
                                                    }`}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </Box>

                                            <TextField
                                                fullWidth
                                                label="Title"
                                                value={preview.title}
                                                onChange={(e) =>
                                                    updatePreview(
                                                        index,
                                                        "title",
                                                        e.target.value
                                                    )
                                                }
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />

                                            <TextField
                                                fullWidth
                                                label="Description"
                                                value={preview.description}
                                                onChange={(e) =>
                                                    updatePreview(
                                                        index,
                                                        "description",
                                                        e.target.value
                                                    )
                                                }
                                                size="small"
                                                multiline
                                                rows={2}
                                                sx={{ mb: 1 }}
                                            />

                                            <TextField
                                                fullWidth
                                                label="Target URL"
                                                value={preview.target_url}
                                                onChange={(e) =>
                                                    updatePreview(
                                                        index,
                                                        "target_url",
                                                        e.target.value
                                                    )
                                                }
                                                size="small"
                                                placeholder="https://example.com"
                                            />

                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    display: "flex",
                                                    gap: 1,
                                                }}
                                            >
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={<Delete />}
                                                    onClick={() =>
                                                        removeFile(index)
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || previews.length === 0}
                    startIcon={
                        loading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <CloudUpload />
                        )
                    }
                >
                    {loading ? "Uploading..." : `Upload ${previews.length} Ads`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkUpload;
